// PANEL A — THE ROLL. The BUILD track's foundation and the ENLIVEN track's gate:
// load (and rebuild) the roster freely, then FREEZE it once, which is a point of
// no return and the thing the attendance register and every Open wait on.
//
// ── THREE STATES, AND THE MIDDLE ONE THE WORKER CANNOT REPORT ──────────────
// A roll is [not loaded] → [loaded] → [frozen]. Frozen is detected by asking the
// attendance register (it answers only once the roll is frozen), so this panel
// KNOWS frozen. It does NOT know loaded-vs-empty on a fresh page: the worker has no
// pre-freeze elector-count read, so a positive count exists only after a load made
// THIS session. That is why Freeze is offered only alongside a count this panel can
// show — freezing a roll it cannot confirm is loaded would earn `clergy_house_empty`
// and teach the operator to press through a refusal.
//
// ── TWO WAYS TO BUILD THE ROLL, AND ONE OF THEM WORKS TODAY ────────────────
// The ZOHO PULL is the next phase (P3b): its button is kept because it is where the
// flow will begin, and a press today reports "not yet available" rather than failing
// — the parent catches the 404 and hands down the note. The PASTED ROSTER is the
// path that needs no credentials and works now: the registrar exports Contacts to
// CSV from the Zoho UI and pastes it here. Neither is a stub and neither replaces
// the other; the note above them says which is which, so a working screen is not
// read as a half-built one.
//
// ── THE BROWSER FINDS THE BAD LINES, BECAUSE THE WORKER CANNOT ─────────────
// `POST …/roll` refuses the WHOLE roster with ONE word and names no row, so a
// 300-line paste with one bad address comes back as `email_malformed` with nothing
// to act on. `./roster.ts` therefore mirrors every row-level refusal client-side and
// reports them ALL AT ONCE, by line number — the discipline `validateDraft` follows
// in `spec.ts`. This file renders that answer and holds no rule of its own.
//
// ── LOADING SAYS WHAT IT DESTROYS EVERY TIME, COUNT OR NO COUNT ────────────
// A (re)load is rebuildable working state, so it takes no modal — the load button
// and the hint beside it say in plain words that loading REPLACES the whole roll,
// and they say it whether or not this panel knows the count. That unconditional is
// the whole point: a count exists only after a load made THIS session (above), so on
// a fresh page a roll of eight people can be sitting in the database with nothing on
// screen admitting it. The earlier warning appeared only alongside a count, which
// made it invisible in exactly the case it was written for — the registrar, on a
// live rehearsal, one press from replacing eight electors with one and told nothing
// at the point of action (2026-09-01). The count is still shown when it IS known,
// because that is strictly more information; nothing depends on it. The freeze is
// the one-way act, so it alone stands behind a Cancel-first confirm.
//
// ── PII ────────────────────────────────────────────────────────────────────
// The box holds real addresses on a real roll. Nothing here logs, and NO cell from
// the paste reaches a message: `roster.ts` addresses every problem by LINE and
// COLUMN and describes the offending cell without repeating it. The one exception
// is a Contacts ID a spreadsheet has already destroyed into scientific notation,
// which an anchored pattern proves can hold no address, name or space. This panel
// says that once, above the list, so an operator does not read a missing value as a
// broken tool — the earlier claim here, that the only address on screen was one
// which failed a shape test, was untrue of two of the messages and of the shape
// test itself. `spellCheck` is off so a roster is not shipped to a dictionary
// service word by word, and `autoComplete` is off so it is not offered back on
// somebody else's machine.

import { useMemo, useState, type JSX } from 'react';
import { ConfirmBlock } from './ConfirmBlock';
import { permittedRollActs } from './state';
import { MAX_ELECTORS, parseRoster, type RosterParse } from './roster';
import type { RosterElector } from '../api/client';

/** How many problems are LISTED. Every one is found and counted — the count is
 *  stated in full, above and below the list — but three hundred `<li>`s bury the
 *  first one, which is the one being fixed. */
const PROBLEMS_SHOWN = 20;

interface Props {
  electionId: string;
  frozen: boolean;
  /** The published roll digest — present iff `frozen`. */
  rollDigest: string | null;
  /** Electors loaded THIS session, or `null` if none has been loaded here. */
  loaded: number | null;
  busy: boolean;
  /** The "not yet available" (P3b) or error note for the pull, or `null`. */
  pullNote: string | null;
  onPull: () => void;
  /** Load the roster the operator pasted. The parse is done here; the parent owns
   *  the request, the refusal and the announcement, exactly as it does for the pull. */
  onLoadRoster: (electors: RosterElector[]) => void;
  onFreeze: () => void;
}

export function RollPanel({
  electionId,
  frozen,
  rollDigest,
  loaded,
  busy,
  pullNote,
  onPull,
  onLoadRoster,
  onFreeze,
}: Props): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const [paste, setPaste] = useState('');

  // An UNTOUCHED box is not a roster with problems — it is no roster at all, so it
  // shows nothing. `parseRoster` itself answers `no_electors` for blank text (it is
  // total over its argument); deciding that an empty box says nothing is this
  // surface's job, not the parser's.
  const parse = useMemo<RosterParse | null>(
    () => (paste.trim() === '' ? null : parseRoster(paste)),
    [paste],
  );

  if (frozen) {
    return (
      <section className="setup-panel card" aria-labelledby="setup-roll-heading">
        <h2 className="setup-panel-heading" id="setup-roll-heading">
          The roll
        </h2>
        <p className="setup-panel-state">
          <span className="ballot-dot" aria-hidden="true" /> The roll is frozen.
        </p>
        <p className="setup-panel-note">
          The roll is fixed as the record of who may vote at this sitting. A change to it now is a
          formal amendment, not a reload. Attendance is kept in the register.
        </p>
        <dl className="setup-digest">
          <dt>Frozen roll digest</dt>
          <dd className="result-mono">{rollDigest}</dd>
        </dl>
        <a className="setup-register-link" href={`#register/${encodeURIComponent(electionId)}`}>
          Open the attendance register
        </a>
      </section>
    );
  }

  const acts = permittedRollActs('not-frozen', loaded !== null);

  const problems = parse?.problems ?? [];
  const warnings = parse?.warnings ?? [];
  const shown = problems.slice(0, PROBLEMS_SHOWN);
  const canLoad = parse !== null && problems.length === 0 && parse.electors.length > 0;

  // The textarea points at the hint and at whichever of the two lists is on screen.
  // A token list, so the hint is never lost when a problem appears beside it — and
  // every id in it is UNCONDITIONALLY rendered by the branch that adds it, because an
  // `aria-describedby` naming an element that is not there is itself an axe failure
  // (`aria-valid-attr-value`) as well as silence for the reader it was meant to help.
  // The reading line is deliberately NOT in here: it is a live region and announces
  // its own changes, so listing it too would have it read twice on entering the box.
  const describedBy = ['roll-paste-hint']
    .concat(problems.length > 0 ? ['roll-paste-problems'] : [])
    .concat(warnings.length > 0 ? ['roll-paste-warnings'] : [])
    .join(' ');

  // The LOAD button's own list, on the same rule. The replace warning is first and
  // unconditional — it is the consequence of the press, and it is there whether or
  // not a count is known (see the header).
  const loadDescribedBy = ['roll-load-replaces']
    .concat(problems.length > 0 ? ['roll-paste-problems'] : [])
    .join(' ');

  return (
    <section className="setup-panel card" aria-labelledby="setup-roll-heading">
      <h2 className="setup-panel-heading" id="setup-roll-heading">
        The roll
      </h2>
      <p className="setup-panel-state">
        <span className="ballot-dot ballot-dot-hollow" aria-hidden="true" /> The roll is not frozen
        yet.
      </p>
      <p className="setup-panel-note">
        Build the roll first, correcting and rebuilding it as often as you like. Freezing fixes it
        and cannot be undone, so it is the last thing you do here before the sitting.
      </p>

      {loaded !== null ? (
        <p className="setup-panel-loaded">
          Loaded <strong>{loaded}</strong> {loaded === 1 ? 'elector' : 'electors'} this session.
        </p>
      ) : null}

      {acts.pull ? (
        <>
          <p className="setup-panel-note">
            There are two ways to build it. Pulling it straight from Zoho is the next phase of
            setup; pasting a roster you have exported from Zoho yourself works today. Either way,
            loading replaces the whole roll until you freeze it.
          </p>

          {/* The pull is the SAME act as the paste (POST …/roll) and replaces the
              roll just as completely, so its label says so unconditionally too —
              this panel cannot see a roll it did not load itself (see the header),
              and a label that softened to "Pull" whenever the count was unknown
              would soften on every fresh page. */}
          <div className="setup-roll-actions">
            <button type="button" className="btn" onClick={onPull} disabled={busy}>
              Replace the whole roll from Zoho
            </button>
          </div>

          {pullNote !== null ? (
            <p className="alert" role="status">
              {pullNote}
            </p>
          ) : null}

          {/* THE PASTED ROSTER. A plain textarea with a real <label> whose text is a
              direct child, the hint and the reading beneath it, and the problems the
              parse found — every one of them, addressed by line. */}
          <div className="setup-field">
            <label htmlFor="roll-paste">Paste the roster</label>
            <textarea
              id="roll-paste"
              className="setup-input setup-textarea"
              rows={8}
              value={paste}
              disabled={busy}
              spellCheck={false}
              autoComplete="off"
              aria-describedby={describedBy}
              onChange={(event) => setPaste(event.target.value)}
            />
            <span className="setup-field-hint" id="roll-paste-hint">
              One person on each line: their Zoho Contacts ID, their email address, and their House
              — clergy, laity or bishop — separated by commas or by tabs. A fourth column, May vote,
              is optional: leave it empty for an ordinary member, and put “no” for an Officer, who
              stays on the roll and in their House but outside the vote. If the first line names the
              columns then their order does not matter and any extra columns are ignored, so a whole
              Zoho export can be pasted as it comes. Up to {MAX_ELECTORS} people in one load. No
              names: the roll seats people by Contacts ID.
            </span>
          </div>

          {/* THE READING, in a live region — the panel's own outcome, and a different
              one from the hub's status line above it: this reports what was READ, which
              is local and instant, while the hub reports what was LOADED, which is the
              server's answer. The sentence is built from COUNTS alone, so typing that
              does not change a count re-renders the identical string, React writes no
              DOM, and nothing is announced. */}
          <p className="setup-status" role="status">
            {parse === null ? '' : readingSentence(parse)}
          </p>

          {problems.length > 0 ? (
            <div className="setup-builder-blockers" id="roll-paste-problems">
              <p className="setup-panel-note">
                Fix {problems.length === 1 ? 'this' : `these ${String(problems.length)}`} before the
                roster can be loaded. Nothing has been loaded.
              </p>
              {/* Said ONCE for the whole list rather than in every message: three
                  hundred repetitions of it would be noise, and a message that
                  quietly withholds a value reads as a broken tool. */}
              <p className="setup-field-hint">
                A problem on a line names the line and the column, so you can find it in the box
                above. What the cell holds is not repeated here — a roll is real people’s contact
                details, and this list is read on screen and pasted into messages.
              </p>
              <ul className="setup-blocker-list">
                {shown.map((problem, index) =>
                  problem.at === 'line' ? (
                    <li key={`${problem.code}-${String(problem.line)}-${String(index)}`}>
                      <strong>Line {problem.line}</strong> — {problem.message}
                    </li>
                  ) : (
                    <li key={`${problem.code}-${String(index)}`}>{problem.message}</li>
                  ),
                )}
              </ul>
              {problems.length > shown.length ? (
                <p className="setup-field-hint">
                  Showing the first {shown.length} of {problems.length}. The rest are listed as
                  these are corrected.
                </p>
              ) : null}
            </div>
          ) : null}

          {warnings.length > 0 ? (
            <div className="setup-builder-blockers setup-ahead" id="roll-paste-warnings">
              <p className="setup-panel-note">
                This roster will load. It could not then be frozen, and freezing is what the
                attendance register and every ballot wait on:
              </p>
              <ul className="setup-blocker-list">
                {warnings.map((warning) => (
                  <li key={warning.code}>{warning.message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* THE ACT ITSELF. The button names the consequence and the hint beneath it
              states it in full — both unconditionally, and the button points at the
              hint so it is read out with the control rather than only seen beside it.
              The id is UNCONDITIONALLY rendered by the branch that names it, the rule
              the describedBy above records. */}
          <div className="setup-roll-actions">
            <button
              type="button"
              className="btn"
              disabled={busy || !canLoad}
              aria-describedby={loadDescribedBy}
              onClick={() => {
                if (parse !== null && canLoad) onLoadRoster(parse.electors);
              }}
            >
              Load this roster, replacing the whole roll
            </button>
            <span className="setup-field-hint" id="roll-load-replaces">
              {replaceWarning(loaded)}
            </span>
          </div>
        </>
      ) : null}

      {/* FREEZE — only alongside a count this panel can show (see the header). The
          one-way act, behind a Cancel-first confirm. */}
      {acts.freeze ? (
        confirming ? (
          <ConfirmBlock
            prompt="Freeze the roll as it stands now? This fixes who may vote at this sitting and cannot be undone."
            confirmLabel="Yes, freeze the roll"
            busy={busy}
            onConfirm={onFreeze}
            onCancel={() => setConfirming(false)}
          />
        ) : (
          <button
            type="button"
            className="btn btn-primary setup-primary"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            Freeze the roll
          </button>
        )
      ) : null}
    </section>
  );
}

/**
 * What loading this roster will DESTROY — said at the point of action, and said
 * whether or not this panel knows the number.
 *
 * With a count it is exact rather than a floor: a load replaces the whole roll, so
 * after one made this session the roll IS those electors and no others. WITHOUT a
 * count the panel cannot see whether a roll exists at all (see the header), which is
 * the case that needs the warning most — so it says the replacement plainly and then
 * says why no number is offered, rather than falling silent and reading as "there is
 * nothing here to lose".
 */
function replaceWarning(loaded: number | null): string {
  const tail =
    'and the roll becomes exactly the people in this roster. You can load again until you freeze.';
  if (loaded === null) {
    return `This replaces the whole roll. Everyone on it now will be removed, ${tail} A roll loaded in an earlier session does not show on this screen, so there may already be one.`;
  }
  return `This replaces the whole roll. The ${String(loaded)} ${loaded === 1 ? 'elector' : 'electors'} on it now will be removed, ${tail}`;
}

/**
 * What the paste READS as, in one sentence of counts.
 *
 * It always ends by saying nothing has been loaded — the same fact every refusal in
 * `copy.ts` states first, and the one an operator staring at a wall of problems most
 * needs. The bad-LINE count is distinct from the PROBLEM count because one line can
 * carry several (a missing ID and an unknown House), and reporting "8 problems" over
 * 3 lines as "8 lines" would send the operator looking for five lines that are fine.
 */
function readingSentence(parse: RosterParse): string {
  const { electors, problems, rowsRead } = parse;
  const people = (n: number): string => `${String(n)} ${n === 1 ? 'elector' : 'electors'}`;

  if (problems.length === 0) {
    return `Read ${people(electors.length)}. Nothing has been loaded yet.`;
  }
  // A whole-paste refusal (an empty paste, an oversized one, a header missing a
  // column) is not about particular lines, so it is not reported as a count of them.
  if (problems.some((problem) => problem.at === 'paste')) {
    return 'That roster cannot be read. Nothing has been loaded.';
  }
  // `flatMap` rather than `filter().map()`: a plain filter does not narrow the union,
  // so `problem.line` would not exist on the paste arm.
  const badLines = new Set(
    problems.flatMap((problem) => (problem.at === 'line' ? [problem.line] : [])),
  ).size;
  return `${String(problems.length)} ${problems.length === 1 ? 'problem' : 'problems'} on ${String(badLines)} of the ${String(rowsRead)} lines pasted; ${people(electors.length)} can be read. Nothing has been loaded.`;
}
